import { Link, useSearchParams } from 'react-router';
import type { AdminEditRequest } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  daysWaiting,
  EDIT_REQUEST_STATUS_LABELS,
  replyLink,
  useEditRequests,
  useUpdateEditRequest,
  type EditRequestStatus,
} from '../lib/editRequests.js';
import { formatDate } from '../lib/format.js';

/* ============================================================================
   /edit-requests — what clients have asked us to change.
   ----------------------------------------------------------------------------
   The screen that closes the loop. Until it existed a client could file a
   request through the portal and nobody could read it without opening
   Adminer — which is worse than not offering the button, because the client
   was told it had been sent.

   Ordered oldest-first within open, by the API, because this is a queue people
   work FROM. Newest-first is right for a log and wrong for a to-do list: it
   buries the request that has been waiting longest under the one that arrived
   this morning.
   ========================================================================== */

const STATUSES: EditRequestStatus[] = ['open', 'done'];

export function EditRequestsPage() {
  const [params, setParams] = useSearchParams();

  const statusParam = params.get('status');
  const status = STATUSES.includes(statusParam as EditRequestStatus)
    ? (statusParam as EditRequestStatus)
    : undefined;
  const q = params.get('q') ?? '';

  const { data, isPending, error } = useEditRequests({
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  });
  const update = useUpdateEditRequest();

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? {};

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Change requests</h1>
          <p className="muted">
            {isPending ? 'Loading…' : `${counts.open ?? 0} open · ${counts.done ?? 0} done`}
          </p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setParam('status', undefined)}
        >
          All
        </button>
        {STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            aria-pressed={status === value}
            onClick={() => setParam('status', value)}
          >
            {EDIT_REQUEST_STATUS_LABELS[value]} ({counts[value] ?? 0})
          </button>
        ))}

        <input
          type="search"
          aria-label="Search by business or number"
          placeholder="Business or number"
          defaultValue={q}
          style={{ marginLeft: 'auto', maxWidth: 240 }}
          onChange={(event) => setParam('q', event.target.value.trim() || undefined)}
        />
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the list.'}
        </p>
      ) : null}

      {update.isError ? (
        <p className="alert" role="alert">
          {update.error instanceof ApiError ? update.error.message : 'Could not update it.'}
        </p>
      ) : null}

      {!isPending && items.length === 0 ? (
        <p className="muted">
          {q || status
            ? 'Nothing matches this filter.'
            : 'No change requests yet. Clients send them from the portal.'}
        </p>
      ) : null}

      <div className="stack">
        {items.map((item) => (
          <RequestCard
            key={item.id}
            item={item}
            pending={update.isPending}
            onToggle={() =>
              update.mutate({ id: item.id, status: item.status === 'open' ? 'done' : 'open' })
            }
          />
        ))}
      </div>
    </>
  );
}

function RequestCard({
  item,
  pending,
  onToggle,
}: {
  item: AdminEditRequest;
  pending: boolean;
  onToggle: () => void;
}) {
  const waiting = daysWaiting(item.createdAt);
  /* Only an open request is worth ageing. A finished one having taken eleven
     days is history, not something anyone can act on today. */
  const isOld = item.status === 'open' && waiting >= 3;

  return (
    <article className="card">
      <div className="page-head" style={{ marginBottom: 8 }}>
        <div>
          <Link to={`/clients/${item.clientId}`}>
            <strong>{item.business}</strong>
          </Link>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {item.domain ?? item.projectPlan} · {formatDate(item.createdAt)}
            {item.status === 'open' && waiting > 0 ? ` · waiting ${waiting} days` : ''}
            {item.doneAt ? ` · done ${formatDate(item.doneAt)}` : ''}
          </p>
        </div>
        <span className={isOld ? 'pill pill--warn' : 'pill'}>
          {EDIT_REQUEST_STATUS_LABELS[item.status]}
        </span>
      </div>

      {/* The client's own words, never editable here. An office that can
          rewrite a request is an office that can quietly narrow the one it
          failed to deliver — the API rejects a `message` field too. */}
      <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 12px' }}>{item.message}</p>

      <div className="row">
        <button type="button" className="btn btn--small" disabled={pending} onClick={onToggle}>
          {item.status === 'open' ? 'Mark done' : 'Reopen'}
        </button>
        <a
          className="btn btn--ghost btn--small"
          href={replyLink(item)}
          target="_blank"
          rel="noreferrer"
        >
          Reply on WhatsApp
        </a>
        <Link className="btn btn--ghost btn--small" to={`/projects/${item.projectId}`}>
          Project
        </Link>
      </div>
    </article>
  );
}
